/**
 * DEV-1251 P2. Shared read-only renderer for a Role Rhythm task's
 * checklist steps. Used by both the Engagements `RhythmTaskTray`
 * (where the user explores their role's tasks) and the Action
 * Center `ActionItemTray` (where the user actually works through
 * a due task without leaving the inbox).
 *
 * Single source of truth for the step-row shape:
 *   - Step label (always)
 *   - Required badge when `isRequired` (small chip)
 *   - Step description (when set)
 *   - URL link (when set; renders `urlLabel` as click text + URL tooltip;
 *     falls back to the raw URL as text when no label is set)
 *
 * Read-only by design. Per-step completion checkboxes are deliberately
 * out of scope for this epic (DEV-1251 Q1 design lock 2026-06-08); they
 * ship in a future epic alongside the `role_rhythm_task_step_completions`
 * schema and the gating "task can't complete until required steps complete"
 * enforcement. When that lands, this component grows a `checkedStepGuids`
 * prop + an `onToggle(stepGuid)` callback; the parent owns the state.
 *
 * Returns `null` for an empty step array. Caller decides whether to render
 * a "no steps" empty state above or below — this keeps the component
 * single-purpose and avoids forcing every consumer to suppress when empty.
 */

import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { RhythmStepDetail } from "../../api/roleRhythms";
import styles from "./RhythmStepList.module.css";

interface RhythmStepListProps {
  steps: RhythmStepDetail[];
}

function RhythmStepList({ steps }: RhythmStepListProps) {
  const { t } = useAppTranslation("pages");
  if (!steps || steps.length === 0) return null;

  return (
    <ol className={styles.list}>
      {steps.map((step) => {
        // DEV-1240. Friendly link rendering: when urlLabel is set, it's
        // the visible click target and the raw URL surfaces via the
        // title tooltip (security-sensible — users see where they're
        // going before clicking). When unset, fall back to the URL
        // itself as the link text.
        const url = step.url?.trim();
        const linkText = step.urlLabel?.trim() || url;
        return (
          <li key={step.stepGuid} className={styles.row}>
            <div className={styles.headerRow}>
              <span className={styles.label}>{step.label}</span>
              {step.isRequired && (
                <span className={styles.requiredBadge}>
                  {t("engagements.rhythmStepRequired", {
                    defaultValue: "Required",
                  } as never)}
                </span>
              )}
            </div>
            {step.description && (
              <span className={styles.desc}>{step.description}</span>
            )}
            {url && linkText && (
              <a
                className={styles.link}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={url}
              >
                🔗 {linkText}
              </a>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default RhythmStepList;
